const globalTranslationState = {
  isTranslationLoading: ref<boolean>(false),
  showTranslationWarning: ref<boolean>(false),
  // Remove isFirstTimeLanguageChange since we don't need it anymore
  translationLoadTimeout: ref<NodeJS.Timeout | null>(null),
}

export default function useTranslationLoader() {
  const setTranslationLoading = (loading: boolean): void => {
    globalTranslationState.isTranslationLoading.value = loading
  }

  const handleLanguageChangeWithLoading = async (
    languageCode: string,
    setLanguageFunction: (code: string) => void,
  ): Promise<void> => {
    // Clear any existing timeout
    if (globalTranslationState.translationLoadTimeout.value) {
      clearTimeout(globalTranslationState.translationLoadTimeout.value)
    }

    // Check if we should show the warning (every time for non-English, unless dismissed permanently)
    const shouldShowWarning = languageCode !== 'en' && checkShouldShowWarning()

    // Only show loading for non-English languages
    if (languageCode !== 'en') {
      // Set loading state
      setTranslationLoading(true)
    }

    try {
      // Call the translation function
      setLanguageFunction(languageCode)

      // Wait for translations to load with timeout (only for non-English)
      if (languageCode !== 'en') {
        await new Promise<void>((resolve) => {
          globalTranslationState.translationLoadTimeout.value = setTimeout(() => {
            resolve()
          }, 2000) // 2 second timeout for translation loading
        })
      }
    }
    finally {
      setTranslationLoading(false)

      // Show warning dialog AFTER loading is complete
      if (shouldShowWarning) {
        globalTranslationState.showTranslationWarning.value = true

        // Show the modal after loading is done
        const { $modal } = useNuxtApp()
        setTimeout(() => {
          $modal.show('translation-warning-dialog')
        }, 100) // Small delay to ensure loading overlay is fully hidden
      }
    }
  }

  const dismissTranslationWarning = (): void => {
    globalTranslationState.showTranslationWarning.value = false
    // Store preference to not show again
    if (typeof window !== 'undefined') {
      localStorage.setItem('hideTranslationWarning', 'true')
    }
  }

  const checkShouldShowWarning = (): boolean => {
    // Check if user has permanently dismissed the warning
    if (typeof window !== 'undefined') {
      const hideWarning = localStorage.getItem('hideTranslationWarning')
      if (hideWarning === 'true') {
        return false
      }
    }

    // Always show warning for non-English languages unless permanently dismissed
    return true
  }

  // Reset function for testing purposes
  const resetWarningState = (): void => {
    globalTranslationState.showTranslationWarning.value = false
    if (typeof window !== 'undefined') {
      localStorage.removeItem('hideTranslationWarning')
    }
  }

  return {
    isTranslationLoading: readonly(globalTranslationState.isTranslationLoading),
    showTranslationWarning: readonly(globalTranslationState.showTranslationWarning),
    setTranslationLoading,
    handleLanguageChangeWithLoading,
    dismissTranslationWarning,
    checkShouldShowWarning,
    resetWarningState,
  }
}
